import React, { PureComponent } from 'react';
import {
    Text,
    TouchableOpacity,
    View,
    Platform,
    ActivityIndicator,
    TextInput,
    FlatList,
    Image
} from 'react-native-web';
import PropTypes from 'prop-types';
import Icon from 'react-native-vector-icons/MaterialIcons';

import { CityPickerModal, InScreenModal, NavBar } from '../../components';
import LoadingView from '../../components/component_loading_view';

import { showAppError } from '../../utils/util_helpers';
import { getBreweryList } from '../../modules/api_breweries';
import { getLogoPhoto } from '../../utils/util_truck_helpers';

import GLOBAL from '../../configs/config_global';

export default class BrewerySearchModal extends PureComponent {
    // region Props
    static propTypes = {
        onBrewerySelected: PropTypes.func
    };

    static defaultProps = {
        onBrewerySelected: () => {}
    };
    // endregion

    // region Constructor
    state = {
        showModal: false,
        searchText: '',
        isLoadingBreweries: false,
        isLoadingBreweryCoordinates: false,
        breweryResult: [],
        typing: false,
        typingTimeout: null,
        totalResult: 0,
        currentPage: 0,
        lastPage: 0,
        selectedCityID: GLOBAL.MAIN?.state.truckData.city?.[0]?.id || 17828
    };
    // endregion

    // region Function - Hide Modal
    hideModal = () => this.setState({ showModal: false });
    showModal = () => this.setState({ showModal: true });
    // endregion

    // region Function - Pick Brewery
    pickBrewery(breweryData) {
        // Return data
        this.props.onBrewerySelected({
            activityData: null,
            breweryData,
            address: breweryData.name + '\n' + breweryData.location,
            latitude: breweryData.latitude || -1,
            longitude: breweryData.longitude || -1
        });

        // Close modal
        this.hideModal();
    }
    // endregion

    // region Function - Search for breweries
    searchForBreweries(text) {
        // If nothing was input, ignore
        if (!text.trim()) {
            this.setState({ breweryResult: [], searchText: '', currentPage: 0, lastPage: 0, totalResult: 0 });
            return;
        }

        // Start searching
        this.setState({ isLoadingBreweries: true, searchText: text, breweryResult: [], typing: false, currentPage: 0, lastPage: 0, totalResult: 0 }, () => {
            this.searchInput?.blur();

            // Start calling API
            getBreweryList(1, 20, this.state.searchText, GLOBAL.CITIES[this.state.selectedCityID].slug, GLOBAL.CITIES[this.state.selectedCityID].state.short_name)
                .then((resultObj) => {
                    this.setState({
                        isLoadingBreweries: false,
                        breweryResult: resultObj.data,
                        currentPage: resultObj.currentPage,
                        lastPage: resultObj.lastPage,
                        totalResult: resultObj.totalResult
                    },
                    () => {
                        if (this.state.breweryResult.length > 0) {
                            this.breweryFlatlist?.scrollToIndex({ index: 0, animated: false });
                        }
                    });
                })
                .catch((error) => {
                    this.setState({ isLoadingBreweries: false, breweryResult: [] });
                    showAppError(error);
                });
        });
    }

    keepFetchingBreweryData() {
        // Cancel if there is no internet connection
        if (!GLOBAL.MAIN?.state.isConnected || (this.state.currentPage >= this.state.lastPage) || this.state.isLoadingBreweries) return;

        // Start calling API
        this.setState({ isLoadingBreweries: true });
        getBreweryList(this.state.currentPage + 1, 20, this.state.searchText, GLOBAL.CITIES[this.state.selectedCityID].slug, GLOBAL.CITIES[this.state.selectedCityID].state.short_name)
            .then((resultObj) => {
                this.setState({
                    breweryResult: this.state.breweryResult.concat(resultObj.data),
                    currentPage: resultObj.currentPage,
                    lastPage: resultObj.lastPage,
                    totalResult: resultObj.totalResult,
                    isLoadingBreweries: false
                });
            })
            .catch((error) => {
                this.setState({ isLoadingBreweries: false });

                // Inform the user
                GLOBAL.MAIN?.toast.show('Unable to fetch more data from server.\n\nError message: ' + error, 3000);
            });
    }
    // endregion

    // region Render Search Bar
    renderSearchBar() {
        return(
            <View
                ref={(comp) => this.brewerySearchBar = comp}
                style={[{
                    alignSelf: 'stretch', alignItems: 'center', flexDirection: 'row',
                    backgroundColor: GLOBAL.COLOR.BASIC.WHITE_TWO,
                    borderRadius: 5, borderColor: 'rgba(0, 0, 0, 0.08)', height: 64,
                    margin: 16, marginTop: 0, paddingHorizontal: 8
                }]}>

                {/* Search Icon */}
                {
                    this.state.isLoadingBreweries ?
                        <ActivityIndicator
                            animating
                            color={GLOBAL.COLOR.MAIN}
                            style={{ marginHorizontal: 4 }}
                            size={32}
                        />
                        :
                        <Icon
                            name={'search'}
                            size={32}
                            color={this.state.searchText ? GLOBAL.COLOR.TEXT_GRAY : GLOBAL.COLOR.BASIC.PINKISH_GREY}
                            style={{ marginLeft: 4 }}
                        />
                }

                {/* Search Text Input */}
                <TextInput
                    ref={(comp) => this.searchInput = comp}
                    autoCorrect={false}
                    onChangeText={(text) => {
                        const self = this;
                        if (self.state.typingTimeout) {
                            clearTimeout(self.state.typingTimeout);
                        }

                        if (!text.trim()) {
                            self.setState({ searchText: '', breweryResult: [], typing: false });
                            return;
                        }

                        self.setState(Object.assign({
                            typing: true,
                            typingTimeout: setTimeout(() => this.searchForBreweries(text), 750),
                            searchText: text
                        }));
                    }}
                    onSubmitEditing={() => {
                        if (this.state.typingTimeout) clearTimeout(this.state.typingTimeout);
                        this.searchForBreweries(this.state.searchText);
                    }}
                    value={this.state.searchText}
                    editable={!this.state.isLoadingLocation}
                    placeholder={`Enter brewery name, type, etc.`}
                    placeholderTextColor={GLOBAL.COLOR.BASIC.PINKISH_GREY}
                    underlineColorAndroid={'transparent'}
                    style={{ outlineStyle: 'none', fontSize: 18, color: GLOBAL.COLOR.BASIC.WARM_GREY, marginLeft: 8, flex: 1, marginVertical: Platform.OS === 'android' ? -5 : 0 }}
                />

                {/* Clear Text Button */}
                {
                    (this.state.searchText.trim() || (this.state.currentPage > 0)) &&
                    <TouchableOpacity
                        style={{ marginRight: 2 }}
                        onPress={() => this.setState(Object.assign({ breweryResult: [], searchText: '', currentPage: 0, totalPage: 0, lastPage: 0 }))}>
                        <Icon
                            name={'close'}
                            size={28}
                            color={GLOBAL.COLOR.BASIC.PINKISH_GREY}
                        />
                    </TouchableOpacity>
                }

                {/* City Selector */}
                <TouchableOpacity
                    onPress={() => this.cityPicker?.showModal()}
                    style={{ flexDirection: 'row', alignItems: 'center', marginRight: 8 }}>
                    {/* Divider */}
                    <View style={{ height: 16, width: 1, marginVertical: 2, backgroundColor: GLOBAL.COLOR.TEXT_LIGHT_GRAY, marginRight: 16, marginLeft: 8 }}/>

                    {/* City Name */}
                    <Text style={{ color: 'black', fontWeight: '500' }}>
                        {GLOBAL.CITIES[this.state.selectedCityID].name}
                    </Text>
                </TouchableOpacity>
            </View>
        );
    }
    // endregion

    // region Render Brewery Card
    renderBreweryCard(item) {
        let logoSize = 40;

        return(
            <TouchableOpacity
                onPress={() => this.pickBrewery(item)}
                style={{ width: '100%', flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, padding: 16 }}>
                {/* Image */}
                <Image
                    source={getLogoPhoto(item)}
                    style={{ width: logoSize, height: logoSize, borderRadius: logoSize / 2, backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}
                    resizeMode={'cover'}
                />

                {/* Info */}
                <View style={{ flex: 1, marginLeft: 16 }}>
                    {/* Name and Rating */}
                    <Text style={{ fontWeight: '500', fontSize: 18, alignSelf: 'stretch' }} numberOfLines={2}>
                        {item.name}
                    </Text>

                    {/* Location */}
                    <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, fontSize: 16, alignSelf: 'stretch', marginTop: 8 }} numberOfLines={1}>
                        {item.location || ''}
                    </Text>
                </View>
            </TouchableOpacity>
        );
    }
    // endregion

    // region Render City Picker
    renderCityPicker() {
        return(
            <CityPickerModal
                ref={(comp) => (this.cityPicker = comp)}
                singleSelect={true}
                liteMode={true}
                onCitySelected={(id) => this.setState({ selectedCityID: id })}
            />
        )
    }
    // endregion

    // region MAIN RENDER FUNCTION
    render() {
        return(
            <InScreenModal visible={this.state.showModal}>
                {/* Nav Bar */}
                <NavBar
                    title={'Brewery Search'}
                    horizontalSpacing={16}
                    hideBorder={true}
                    leftButtonOnPress={() => this.hideModal()}
                    leftButtonIconName={'close'}
                    ignoreTopBarHeight={true}
                />

                {/* Content */}
                <View style={{ flex: 1, width: '100%', backgroundColor: 'white' }}>
                    {/* Search Bar */}
                    {this.renderSearchBar()}

                    {/* Brewery List */}
                    {
                        (this.state.breweryResult.length > 0) ?
                            <FlatList
                                ref={(comp) => this.breweryFlatlist = comp}
                                style={{ width: '100%', flex: 1 }}
                                contentContainerStyle={{ paddingBottom: 24 }}
                                data={this.state.breweryResult}
                                renderItem={({ item }) => this.renderBreweryCard(item)}
                                showsVerticalScrollIndicator={false}
                                keyExtractor={(item) => item.id.toString()}
                                onEndReached={() => this.keepFetchingBreweryData()}
                                onEndReachedThreshold={0.2}
                                ListFooterComponent={() => (
                                    (this.state.isLoadingBreweries && this.state.currentPage > 0) ?
                                        <View style={{ flexDirection: 'row', alignItems: 'center', padding: 16, justifyContent: 'center' }}>
                                            <ActivityIndicator color={GLOBAL.COLOR.MAIN}/>

                                            <Text style={{ fontWeight: '500', color: GLOBAL.COLOR.MAIN, fontSize: 16, marginLeft: 8 }}>
                                                Fetching more breweries
                                            </Text>
                                        </View>
                                        :
                                        null
                                )}
                            />
                            :
                            <View style={{ flex: 1, width: '100%', alignItems: 'center', justifyContent: 'center' }}>
                                <Text style={{ textAlign: 'center', alignSelf: 'center', color: GLOBAL.COLOR.TEXT_LIGHT_GRAY }}>
                                    {(this.state.currentPage > 0) ? 'No results found' : (this.state.typing ? '' : 'Start typing to search')}
                                </Text>
                            </View>
                    }
                </View>

                {/* City Picker Modal */}
                {this.renderCityPicker()}

                {/* Loading View */}
                {this.state.isLoadingBreweryCoordinates && <LoadingView isAbsolute={true} description={'Retrieving location data'}/>}
            </InScreenModal>
        );
    }
    // endregion
}
